import { proxy } from "valtio"
import { EmblemApi } from "../api/wizard"
import { Steps, Views } from "../constants"
import { Circle, CircleData, ColorTarget, ContactInfo, Dimension, Emblem3d, GeneralInformation, Step, StepOption, UploadedImage } from "../types/wizard"
import { validationMessages } from "../config/validationMessages";
import { sizeStandardValidation } from "../actions/wizard";
import { designPrice, getVolumePrices } from "../api/art";
import { DesignDoc, getPriceAndDiscount } from "../types/emblemData";
import { isStepHiddenByRules } from "../config/productRules";

// Default store state
const defaultState = {
  target: {} as ColorTarget,
  view: Views.EMBLEMS,
  designRequest: {} as any,
  step: {} as Step,
  loading: false,
  stepIndex: -1,
  steps: [] as Step[],
  emblemData: {} as Record<string, any>,
  requireIndication: false as boolean,
  isEmblemLeather: false as boolean,
  editedCircleColor: null as CircleData | null,
  hoveredColorId: 0 as number,
  pricing: {} as any,
  errors: {} as Record<string, string[]>,
  validationMessage: "",
  activePrice: {} as any,
  uploadedImageDimensions: null as
    {
      width: number;
      height: number;
      contentWidth: number;
      contentHeight: number;
      contentAspectRatio: number;
    } | null,
  artName: '' as string,
  contactData: null as ContactInfo | null
};

export const wizardStore = proxy({ ...defaultState });

export function validateWizardSteps(stepIdToValidate?: string): boolean {
  const { steps, emblemData } = wizardStore;

  const currentStepIds = new Set(steps.map(step => step.id));
  const errorMessages = validationMessages.steps
    .filter(step => currentStepIds.has(step.id))
    .reduce<Record<string, string[]>>((acc, step) => {
      acc[step.id] = step.errors;
      return acc;
    }, {});

  let messages: string[] = [];
  let newErrors: Record<string, string[]> = stepIdToValidate
    ? { ...wizardStore.errors }
    : {};

  const stepsToValidate = stepIdToValidate
    ? steps.filter((step) => step.id === stepIdToValidate)
    : steps;

  stepsToValidate.forEach((step) => {
    const stepId = step.id;

    if (isStepHiddenByRules(stepId, emblemData)) {
      delete newErrors[stepId];
      return;
    }

    const isEmpty =
      !emblemData[stepId] ||
      (typeof emblemData[stepId] === "object" &&
        Object.keys(emblemData[stepId]).length === 0);

    let isInvalid = isEmpty;
    if (stepId === Steps.SizeStandard) {
      isInvalid = sizeStandardValidation();
    } else if (stepId === Steps.ThreadColors) {
      const allowWEItoChoose = emblemData[stepId]?.allowWEItoChoose ?? false;
      if (!allowWEItoChoose) {
        isInvalid = isEmpty || Object.keys(emblemData[stepId]?.items)?.length === 0;
      } else {
        isInvalid = false;
      }
    }
    else if ([Steps.Emblem3d, Steps.EmblemPrintStitch, Steps.EmblemLeather].includes(stepId)) {
      const allowWEItoChoose = emblemData[stepId]?.allowWEItoChoose ?? false;
      if (!allowWEItoChoose) {
        const dataArray = stepId === Steps.EmblemLeather
          ? emblemData[stepId]?.indicators
          : emblemData[stepId]?.items;

        const hasData = dataArray && Array.isArray(dataArray) && dataArray.length > 0;

        if (!hasData) {
          isInvalid = true;
        } else {
          const allItemsHaveInstructions = dataArray.every((item: any) => {
            return item.instructions && item.instructions.trim() !== "";
          });

          isInvalid = !allItemsHaveInstructions;
        }
      } else {
        isInvalid = false;
      }
    }

    if (isInvalid) {
      if (errorMessages[stepId]) {
        newErrors[stepId] = errorMessages[stepId];
        messages.push(...errorMessages[stepId]);
      }
    } else {
      delete newErrors[stepId];
    }
  });

  wizardStore.errors = newErrors;
  wizardStore.validationMessage = Object.values(newErrors).flat().join(" ");

  return Object.keys(newErrors).length === 0;
}

export const resetValidations = () => {
  wizardStore.errors = {};
  wizardStore.validationMessage = "";
}

export async function fetchPriceFromHost() {
  try {
    wizardStore.loading = true;
    const customerId = localStorage.getItem("customerId") ?? '';
    const { emblemData, designRequest, artName } = wizardStore;
    const wizardData = DesignDoc.transformToDesignDoc(emblemData, designRequest, customerId, artName);

    const data = {
      action: 'volumePricesQuery',
      productId: wizardData.itemNo,
      items: {}
    }

    window.parent.postMessage(data, '*');

  } catch (error) {
    console.error("Error fetching price:", error);
  } finally {
    wizardStore.loading = false;
  }
}

export async function fetchPriceFromSANA() {
  try {
    const customerId = localStorage.getItem("customerId") ?? '';
    const { emblemData, designRequest, artName } = wizardStore;
    const wizardData = DesignDoc.transformToDesignDoc(emblemData, designRequest, customerId, artName);
    const ret = await getVolumePrices(wizardData.itemNo as string);

    if (ret.success) {
      const pricingData = ret.response.catalog.volumePrices;

      if (!Array.isArray(pricingData)) {
        console.error('pricingData is not an array:', pricingData);
        return;
      }

      const currentQuantity = wizardStore.pricing?.quantity || 1;
      const activePrice = getPriceAndDiscount(pricingData, emblemData.SizeStandard, emblemData[Steps.Backing]?.name, currentQuantity);
      wizardStore.pricing = {
        data: pricingData,
        quantity: currentQuantity
      };
      wizardStore.activePrice = activePrice;
    }

  } catch (error) {
    console.error("Error fetching price:", error);
  }
}

export async function fetchPrice() {
  try {
    const customerId = localStorage.getItem("customerId") ?? '';
    const { emblemData, designRequest, artName } = wizardStore;
    const wizardData = DesignDoc.transformToDesignDoc(emblemData, designRequest, customerId, artName);

    const response = await designPrice(wizardData);
    if (!response.success || !response.pricing) {
      console.error("Pricing API error", response);
      return;
    }

    const pricing = response.pricing;
    wizardStore.pricing = pricing;
  } catch (error) {
    console.error("Error fetching price:", error);
  }
}

export async function addToCart() {
  try {
    wizardStore.loading = true;
    const { pricing } = wizardStore;

    const {
      itemNo,
      variantId,
      UofM,
      price,
      quantity,
      description,
      imageUrl,
      designDocId,
      token
    } = pricing;

    const sanaDoc = {
      "itemNo": itemNo,
      "variantId": variantId,
      "UofM": UofM,
      "price": price,
      "quantity": quantity,
      "description": description,
      "imageUrl": imageUrl,
      "designDocId": designDocId,
      "designDocModified": null,
      "token": token
    }

    const itemGroup = { items: [{ productId: sanaDoc.itemNo, uomId: sanaDoc.UofM, ...sanaDoc }] }
    parent.postMessage(itemGroup, '*');

    console.log('parent.postMessage', JSON.stringify(itemGroup, null, 2));
  } catch (error) {
    console.error("Error fetching price:", error);
  } finally {
    wizardStore.loading = false;
  }
}

export const saveImageDimensions = (dimensions: {
  width: number;
  height: number;
  contentWidth: number;
  contentHeight: number;
  contentAspectRatio: number;
} | null) => {
  wizardStore.uploadedImageDimensions = dimensions;
};

export const getImageAspectRatio = (): number | null => {
  if (!wizardStore.uploadedImageDimensions) return null;

  const { contentAspectRatio, width, height } = wizardStore.uploadedImageDimensions;

  if (contentAspectRatio) {
    return contentAspectRatio;
  }

  return width / height;
};

export const clearImageDimensions = () => {
  wizardStore.uploadedImageDimensions = null;
};

export const updateArtName = (artName: string) => {
  wizardStore.artName = artName;
};

export const addTarget = (stepId: string, circle: Circle) => {
  wizardStore.target = {
    ...wizardStore.target,
    [stepId]: { circle }
  };
};

export const resetWizardStore = () => {
  Object.assign(wizardStore, defaultState);
};

export const startFromProductType = (stepId: string, newData: any) => {
  const existingData = wizardStore.emblemData[stepId] || {};
  wizardStore.emblemData = {
    [stepId]: {
      ...existingData,
      ...newData,
    },
  };
};

export const goToStep = (stepId: string) => {
  const stepPosition = wizardStore.steps.findIndex((s) => s.id === stepId);
  wizardStore.step = wizardStore.steps[stepPosition];
}

export const saveEmblemData = (stepId: string, newData: any) => {
  if (newData == null) {
    const updatedData = { ...wizardStore.emblemData };
    delete updatedData[stepId];
    wizardStore.emblemData = updatedData;
  } else {
    const existingData = wizardStore.emblemData[stepId] || {}
    wizardStore.emblemData = {
      ...wizardStore.emblemData,
      [stepId]: {
        ...existingData,
        ...newData
      }
    }
  }
}

export const removeStepEmblemData = (stepId: string) => {
  const existingData = wizardStore.emblemData[stepId];
  if (existingData) delete wizardStore.emblemData[stepId];
}

export const saveInstructions = (stepId: string, instructions: string) => {
  if (stepId === Steps.DesignType)
    wizardStore.designRequest.createDesign.specialInstructions = instructions;
  else
    wizardStore.designRequest.alternateImage.specialInstructions = instructions;
}

export const editColorFromDetails = (circleData: CircleData | null) => {
  wizardStore.editedCircleColor = circleData;
}

export const setHoveredColor = (circleId: number) => {
  wizardStore.hoveredColorId = circleId;
}

export const checkIfColorIsBeingUsed = (color: CircleData) => {
  const { requireIndication, emblemData } = wizardStore;

  if (requireIndication && emblemData[Steps.Emblem3d]) {
    const indications: CircleData[] = emblemData[Steps.Emblem3d].items;
    const existColor = indications?.find(i => i.color === color.color);

    if (existColor) {
      return true;
    }
  }
  return false;
};

export async function getSteps({ updateSteps }: { updateSteps: boolean }) {
  const steps = await EmblemApi.getSteps({ updateSteps }) as Step[]
  wizardStore.steps = steps
  wizardStore.stepIndex = 0
  wizardStore.step = wizardStore.steps[wizardStore.stepIndex]
  wizardStore.loading = false
}

export async function getNewDesignRequest() {
  try {
    const designRequest = await EmblemApi.getNewDesignRequest();
    if (!designRequest) {
      throw new Error("No design request returned from API");
    }
    wizardStore.designRequest = designRequest;
  } catch (error: any) {
    throw new Error(`Error setting new design request: ${error.message}`);
  }
}

export const saveGeneralInformation = (generalInfo: GeneralInformation) => {
  Object.keys(generalInfo).forEach((key) => {
    if (generalInfo[key as keyof GeneralInformation] !== undefined) {
      wizardStore.designRequest.generalInformation[key as keyof GeneralInformation] =
        generalInfo[key as keyof GeneralInformation];

      wizardStore.designRequest.description = generalInfo['description'];
    }
  });

  wizardStore.designRequest.approvedTermsConditions = true;
};

export const uploadImage = async (file: File, uploadImageId: string) => {
  const id = uploadImageId;
  const imageResult = await EmblemApi.uploadImage(file, id);

  if (imageResult) {
    wizardStore.designRequest.uploadedImage = imageResult;
  }
};

export function cleanupRemovedSteps(oldSteps: Step[], newSteps: Step[]) {
  const newStepIds = new Set(newSteps.map(step => step.id));
  const removedStepIds = oldSteps
    .map(step => step.id)
    .filter(stepId => !newStepIds.has(stepId));

  removedStepIds.forEach(stepId => {
    if (wizardStore.emblemData[stepId]) {
      delete wizardStore.emblemData[stepId];
    }
  });

  const newErrors = { ...wizardStore.errors };
  removedStepIds.forEach(stepId => {
    if (newErrors[stepId]) {
      delete newErrors[stepId];
    }
  });
  wizardStore.errors = newErrors;
}

export async function nextStep(lastOption?: StepOption, currentStepId?: string) {
  const { steps } = wizardStore;

  if (!steps || steps.length === 0) {
    throw new Error("No steps available in the wizard.");
  }

  const stepPosition = steps.findIndex((s) => s.id === currentStepId);
  if (stepPosition !== -1) {
    wizardStore.stepIndex = stepPosition + 1;
  }

  if (lastOption && lastOption.updateSteps) {
    try {
      wizardStore.loading = true;
      const steps = await EmblemApi.getSteps(wizardStore.designRequest);
      if (!steps || steps.length === 0) {
        throw new Error("Failed to fetch steps or no steps returned.");
      }

      const oldSteps = wizardStore.steps;
      wizardStore.steps = steps as Step[]
      wizardStore.requireIndication = steps.some((s: Step) => s.id === Steps.Emblem3d || s.id === Steps.EmblemPrintStitch);
      wizardStore.isEmblemLeather = steps.some((s: Step) => s.id === Steps.EmblemLeather);

      cleanupRemovedSteps(oldSteps, steps);
    } catch (error: any) {
      throw new Error(`Error updating steps: ${error?.message}`);
    } finally {
      wizardStore.loading = false;
    }
  }

  if (wizardStore.stepIndex < 0 || wizardStore.stepIndex > wizardStore.steps.length) {
    return;
  }

  wizardStore.step = wizardStore.steps[wizardStore.stepIndex];
}

export function previousStep() {
  wizardStore.stepIndex--
  wizardStore.step = wizardStore.steps[wizardStore.stepIndex]
}

export function setProductClass(productClass: StepOption, currentStepId: string) {
  wizardStore.designRequest.productClass = productClass;
  wizardStore.designRequest.productClassId = productClass?.id;
  nextStep(productClass, currentStepId);
}

export function setProductSubType(productSubType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productType = productSubType;
  wizardStore.designRequest.productTypeId = productSubType?.id;
  nextStep(productSubType, currentStepId)
}

export function setSizeStandard(dimensions?: Dimension) {
  wizardStore.designRequest.productOptions.dimensions = {
    haveWEISizeDesign: false, // TODO: adjust to logic
    width: dimensions?.width ? dimensions?.width : null,
    widthScale: dimensions?.scaleWidth ? dimensions?.scaleWidth : null,
    height: dimensions?.height ? dimensions?.height : null,
    heightScale: dimensions?.scaleHeight ? dimensions?.scaleHeight : null,
  };
}

export function setFabricColor(fabricColor: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.fabricColor = fabricColor;
  nextStep(fabricColor, currentStepId)
}

export function setBacking(backing: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.backing = backing;
  nextStep(backing, currentStepId)
}

export function setDesignType(designType: StepOption, currentStepId: string) {
  wizardStore.designRequest.createDesign.createDesignOption = designType.code
  nextStep(designType, currentStepId)
}

export function setImageData(imageData: UploadedImage | null) {
  wizardStore.designRequest.createDesign.uploadedImage = imageData;
}

export function setThreadColors(threadColors: CircleData, index: number) {
  const color =
  {
    ...threadColors?.option,
    x: threadColors?.circle?.x,
    y: threadColors?.circle?.y
  }

  if (index === -1) {
    wizardStore.designRequest.threadColors.colors.push(color);
  } else {
    wizardStore.designRequest.threadColors.colors[index] = color;
  }
}

export function setAllowWEItoChooseColors(currentStepId: string, selected: boolean) {
  const stepId = currentStepId === Steps.ThreadColors ? 'threadColors' : currentStepId;

  // Initialize designRequest[stepId] if it doesn't exist
  if (!wizardStore.designRequest[stepId]) {
    wizardStore.designRequest[stepId] = {
      allowWEItoChoose: false,
      allowWEItoChooseOriginal: false,
      colors: null
    };
  }

  // Initialize emblemData[currentStepId] if it doesn't exist
  if (!wizardStore.emblemData[currentStepId]) {
    wizardStore.emblemData[currentStepId] = {
      allowWEItoChoose: false,
      items: null
    };
  }

  wizardStore.designRequest[stepId].allowWEItoChoose = selected;
  wizardStore.emblemData[currentStepId].allowWEItoChoose = selected;

  if (selected)
    wizardStore.emblemData[currentStepId].items = [];
}

export function set3DEmblemOption(currentStepId: string, selectedOption: string) {
  if (!wizardStore.designRequest.productOptions.emblem3D) {
    wizardStore.designRequest.productOptions.emblem3D = {
      set3DEmblemOption: "IndicateParts",
    };
  }

  wizardStore.designRequest.productOptions.emblem3D.set3DEmblemOption = selectedOption;

  if (!wizardStore.emblemData[currentStepId]) {
    wizardStore.emblemData[currentStepId] = {
      allowWEItoChoose: false,
      items: null
    };
  }

  const allowWEItoChoose = selectedOption === "AllowWEIToChoose";
  wizardStore.emblemData[currentStepId].allowWEItoChoose = allowWEItoChoose;

  if (allowWEItoChoose) {
    wizardStore.emblemData[currentStepId].items = [];
  }
}

export function setEmblemPrintStitchOption(currentStepId: string, selectedOption: string) {
  if (!wizardStore.designRequest.productOptions.emblemPrintStitch) {
    wizardStore.designRequest.productOptions.emblemPrintStitch = {
      setEmblemPrintStitchOption: "IndicateParts",
    };
  }

  wizardStore.designRequest.productOptions.emblemPrintStitch.setEmblemPrintStitchOption = selectedOption;

  if (!wizardStore.emblemData[currentStepId]) {
    wizardStore.emblemData[currentStepId] = {
      allowWEItoChoose: false,
      items: null
    };
  }

  const allowWEItoChoose = selectedOption === "AllowWEIToChoose";
  wizardStore.emblemData[currentStepId].allowWEItoChoose = allowWEItoChoose;

  if (allowWEItoChoose) {
    wizardStore.emblemData[currentStepId].items = [];
  }
}

export function setEmblemLeatherOption(currentStepId: string, selectedOption: string) {
  if (!wizardStore.designRequest.productOptions.emblemLeather) {
    wizardStore.designRequest.productOptions.emblemLeather = {
      setEmblemLeatherOption: "IndicateParts",
    };
  }

  wizardStore.designRequest.productOptions.emblemLeather.setEmblemLeatherOption = selectedOption;

  if (!wizardStore.emblemData[currentStepId]) {
    wizardStore.emblemData[currentStepId] = {
      allowWEItoChoose: false,
      indicators: null
    };
  }

  const allowWEItoChoose = selectedOption === "AllowWEIToChoose";
  wizardStore.emblemData[currentStepId].allowWEItoChoose = allowWEItoChoose;

  if (allowWEItoChoose) {
    wizardStore.emblemData[currentStepId].indicators = [];
  }
}

export function setRequestDetails(requestDetails: StepOption, currentStepId: string) {
  nextStep(requestDetails, currentStepId)
}

export function setEmblem3d(emblem3d: Emblem3d) {
  const currentEmblem3d = wizardStore.designRequest.productOptions.emblem3D || {};

  wizardStore.designRequest.productOptions.emblem3D = {
    ...currentEmblem3d,
    instructions: emblem3d?.instructions !== undefined
      ? emblem3d.instructions
      : currentEmblem3d.instructions || null,
    indicators: emblem3d?.indicators || currentEmblem3d.indicators || [],
  };
}

export function setBorderType(borderType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.borderType = borderType;
  nextStep(borderType, currentStepId)
}

export function setBorderColor(borderColor: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.borderColor = borderColor;
  nextStep(borderColor, currentStepId)
}

export function setShape(shape: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.shapeSizeOther = { id: shape.id }
  nextStep(shape, currentStepId)
}

export function setLayout(layout: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.layout = { id: layout.id }
  nextStep(layout, currentStepId)
}

export function setBackgroundPattern(backgroundPattern: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.backgroundPattern = { id: backgroundPattern.id }
  nextStep(backgroundPattern, currentStepId)
}

export function setSewingChannel(sewingChannel: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.sewingChannel = { id: sewingChannel.id }
  nextStep(sewingChannel, currentStepId)
}

export function setEmblemLeather(emblemLeather: StepOption, currentStepId: string) {
  nextStep(emblemLeather, currentStepId)
}

export function setFabricType(fabricType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.fabricType = { id: fabricType.id }
  nextStep(fabricType, currentStepId)
}

export function setGarmentType(garmentType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.garmentType = { id: garmentType.id }
  nextStep(garmentType, currentStepId)
}

export function setLocation(location: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.location = { id: location.id }
  nextStep(location, currentStepId)
}

export function setHolographicColors(holographicColors: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.holographicColors = { id: holographicColors.id }
  nextStep(holographicColors, currentStepId)
}

export function setHolographicUpload(holographicUpload: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.holographicColors = { id: holographicUpload.id }
  nextStep(holographicUpload, currentStepId)
}

export function setScrolling(scrolling: any) {
  const { scrollingText, scrollingFont, scrollingFontDisplayName } = scrolling;
  wizardStore.designRequest.productOptions = {
    ...wizardStore.designRequest.productOptions,
    scrollingText,
    scrollingFont,
    scrollingFontDisplayName
  };
}

export function setCombination(combination: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.combination = { id: combination.id }
  nextStep(combination, currentStepId)
}

export function setShapeSizeStandard(option: StepOption) {
  wizardStore.designRequest.productOptions.shapeSizeStandard = option;
}

export const setStepCallback = {
  [Steps.ProductType]: setProductClass,
  [Steps.ProductSubtype]: setProductSubType,
  [Steps.Shape]: setShape,
  [Steps.SizeStandard]: setSizeStandard,
  [Steps.FabricColor]: setFabricColor,
  [Steps.FabricType]: setFabricType,
  [Steps.Backing]: setBacking,
  [Steps.DesignType]: setDesignType,
  [Steps.BorderType]: setBorderType,
  [Steps.BorderColor]: setBorderColor,
  [Steps.GarmentType]: setGarmentType,
  [Steps.Location]: setLocation,
  [Steps.Layout]: setLayout,
  [Steps.HolographicColors]: setHolographicColors,
  [Steps.HolographicUpload]: setHolographicUpload,
  [Steps.ThreadColors]: setThreadColors,
  [Steps.SewingChannel]: setSewingChannel,
  [Steps.Scrolling]: setScrolling,
  [Steps.Combination]: setCombination
}